<script setup>
import { ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBotDataSources } from 'dashboard/composables/useBotDataSources';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';
import SourceCard from './components/SourceCard.vue';
import UploadModal from './components/UploadModal.vue';
import ApiSourceModal from './components/ApiSourceModal.vue';
import EntriesTable from './components/EntriesTable.vue';
import StatsBar from './components/StatsBar.vue';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const {
  bots,
  selectedBotId,
  selectedBot,
  dataSources,
  dataEntries,
  stats,
  collections,
  loading,
  uploading,
  meta,
  hasSources,
  fetchBots,
  fetchSources,
  fetchSource,
  uploadFile,
  createApiSource,
  updateApiSource,
  testApi,
  generateExtraction,
  previewExtraction,
  syncSource,
  processSource,
  reprocessSource,
  deleteSource,
  fetchEntries,
  deleteEntry,
  bulkDeleteEntries,
  fetchStats,
  selectBot,
} = useBotDataSources();

const showUploadModal = ref(false);
const showApiModal = ref(false);
const showAddMenu = ref(false);
const showDeleteModal = ref(false);
const sourceToDelete = ref(null);
const sourceToEdit = ref(null);
const activeTab = ref('sources');
const selectedCollection = ref(null);

watch(selectedBotId, async () => {
  selectedCollection.value = null;
  if (activeTab.value === 'entries') {
    await fetchEntries();
  }
});

watch(activeTab, async newTab => {
  if (!selectedBotId.value) return;
  if (newTab === 'entries') {
    await Promise.all([
      fetchEntries({ collection: selectedCollection.value }),
      fetchStats(),
    ]);
  } else {
    await fetchSources();
  }
});

watch(selectedCollection, async () => {
  if (activeTab.value === 'entries' && selectedBotId.value) {
    await fetchEntries({ collection: selectedCollection.value });
  }
});

onMounted(async () => {
  await fetchBots();
  if (bots.value.length > 0 && !selectedBotId.value) {
    await selectBot(bots.value[0].id);
  }
});

const handleBotSelect = async botId => {
  await selectBot(botId);
};

const handleUpload = async ({ file, collection }) => {
  const result = await uploadFile(file, collection);
  showUploadModal.value = false;
  if (result?.id) {
    setSourceStatus(result.id, 'pending');
    await processSource(result.id);
    pollSourceStatus(result.id);
  }
};

const handleCreateApiSource = async (payload, done) => {
  const result = await createApiSource(payload);
  if (typeof done === 'function') done();
  showApiModal.value = false;
  if (result?.id) {
    setSourceStatus(result.id, 'pending');
    await processSource(result.id);
    pollSourceStatus(result.id);
  }
};

const handleUpdateApiSource = async ({ id, ...payload }, done) => {
  await updateApiSource(id, payload);
  if (typeof done === 'function') done();
  showApiModal.value = false;
  sourceToEdit.value = null;
};

const handleEditSource = source => {
  sourceToEdit.value = source;
  showApiModal.value = true;
};

const handleCloseApiModal = () => {
  showApiModal.value = false;
  sourceToEdit.value = null;
};

const handleTestApi = async (payload, callback) => {
  const result = await testApi(payload);
  if (typeof callback === 'function') callback(result);
};

const handleGenerateExtraction = async ({ sampleJson, extractionPrompt }, callback) => {
  const result = await generateExtraction(sampleJson, extractionPrompt);
  if (typeof callback === 'function') callback(result);
};

const handlePreviewExtraction = async ({ sampleJson, extractionConfig }, callback) => {
  const result = await previewExtraction(sampleJson, extractionConfig);
  if (typeof callback === 'function') callback(result);
};

const handleSync = async sourceId => {
  setSourceStatus(sourceId, 'processing');
  await syncSource(sourceId);
  pollSourceStatus(sourceId);
};

const handleProcess = async sourceId => {
  setSourceStatus(sourceId, 'processing');
  await processSource(sourceId);
  pollSourceStatus(sourceId);
};

const handleReprocess = async sourceId => {
  setSourceStatus(sourceId, 'processing');
  await reprocessSource(sourceId);
  pollSourceStatus(sourceId);
};

const setSourceStatus = (sourceId, status) => {
  const index = dataSources.value.findIndex(s => s.id === sourceId);
  if (index !== -1) {
    dataSources.value[index] = { ...dataSources.value[index], status };
  }
};

const handleDeleteSource = sourceId => {
  const source = dataSources.value.find(s => s.id === sourceId);
  sourceToDelete.value = source;
  showDeleteModal.value = true;
};

const confirmDeleteSource = async () => {
  if (sourceToDelete.value) {
    await deleteSource(sourceToDelete.value.id);
    showDeleteModal.value = false;
    sourceToDelete.value = null;
  }
};

const cancelDeleteSource = () => {
  showDeleteModal.value = false;
  sourceToDelete.value = null;
};

const handleDeleteEntry = async entryId => {
  if (confirm(t('OMNI.DATA_SOURCES.CONFIRM_DELETE_ENTRY'))) {
    await deleteEntry(entryId);
  }
};

const handleBulkDelete = async params => {
  const count = params.ids ? params.ids.length : 'all';
  const message = params.collection
    ? `Delete all entries in collection "${params.collection}"?`
    : `Delete ${count} selected entries?`;

  if (confirm(message)) {
    await bulkDeleteEntries(params);
  }
};

const handleSearch = async query => {
  await fetchEntries({ search: query, collection: selectedCollection.value });
};

const handlePageChange = async page => {
  await fetchEntries({ page, collection: selectedCollection.value });
};

const handleFilterCollection = collection => {
  selectedCollection.value = collection;
};

const handlePreview = async sourceId => {
  return previewSource(sourceId);
};

const pollSourceStatus = sourceId => {
  let pollCount = 0;

  const poll = async () => {
    const source = await fetchSource(sourceId);

    if (!source || source.status === 'completed' || source.status === 'failed') {
      await fetchStats();
      return;
    }

    pollCount++;
    if (pollCount > 120) return;

    const delay = pollCount <= 5 ? 2000 : pollCount <= 20 ? 5000 : 10000;
    setTimeout(poll, delay);
  };

  setTimeout(poll, 1000);
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
    case 'processing':
      return 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
    case 'failed':
      return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300';
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
  }
};

const getStatusLabel = status => {
  const labels = {
    pending: t('OMNI.DATA_SOURCES.STATUS.PENDING'),
    processing: t('OMNI.DATA_SOURCES.STATUS.PROCESSING'),
    completed: t('OMNI.DATA_SOURCES.STATUS.COMPLETED'),
    failed: t('OMNI.DATA_SOURCES.STATUS.FAILED'),
  };
  return labels[status] || status;
};
</script>

<template>
  <div class="flex flex-col h-full w-full min-w-0" @click.self="showAddMenu = false">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800"
    >
      <div>
        <h1 class="text-xl font-semibold text-slate-900 dark:text-slate-25">
          {{ t('OMNI.DATA_SOURCES.HEADER') }}
        </h1>
        <p class="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          {{ t('OMNI.DATA_SOURCES.DESCRIPTION') }}
        </p>
      </div>
      <!-- Add Source dropdown -->
      <div
        v-if="selectedBotId && canEditSection('data_sources')"
        class="relative"
      >
        <button
          class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="uploading"
          @click="showAddMenu = !showAddMenu"
        >
          <span v-if="uploading" class="i-lucide-loader-2 animate-spin" />
          <span v-else class="i-lucide-plus" />
          {{ t('OMNI.DATA_SOURCES.ADD_SOURCE') }}
          <span class="i-lucide-chevron-down w-3.5 h-3.5" />
        </button>

        <!-- Dropdown menu -->
        <div
          v-if="showAddMenu"
          class="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 z-10 overflow-hidden"
        >
          <button
            class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            @click="showAddMenu = false; showUploadModal = true"
          >
            <span class="i-lucide-upload text-woot-500 w-4 h-4" />
            {{ t('OMNI.DATA_SOURCES.ADD_FILE') }}
          </button>
          <button
            class="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            @click="showAddMenu = false; sourceToEdit = null; showApiModal = true"
          >
            <span class="i-lucide-globe text-woot-500 w-4 h-4" />
            {{ t('OMNI.DATA_SOURCES.ADD_API') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content with Sidebar -->
    <div class="flex-1 overflow-hidden flex">
      <!-- Bot Sidebar -->
      <div
        class="w-56 border-r border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col flex-shrink-0"
      >
        <div class="p-4 pb-2">
          <h2
            class="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500"
          >
            {{ t('OMNI.DATA_SOURCES.SELECT_BOT') }}
          </h2>
        </div>
        <div class="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
          <button
            v-for="bot in bots"
            :key="bot.id"
            class="w-full text-left px-3 py-2.5 rounded-lg transition-all"
            :class="
              selectedBotId === bot.id
                ? 'bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400 shadow-sm'
                : 'text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm'
            "
            @click="handleBotSelect(bot.id)"
          >
            <div class="flex items-center gap-2.5">
              <div
                class="flex h-7 w-7 items-center justify-center rounded-md"
                :class="
                  selectedBotId === bot.id
                    ? 'bg-woot-100 dark:bg-woot-800/30'
                    : 'bg-slate-100 dark:bg-slate-700'
                "
              >
                <span class="i-lucide-bot w-3.5 h-3.5" />
              </div>
              <span class="text-sm font-medium truncate">{{ bot.name }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Main Area -->
      <div class="flex-1 overflow-hidden flex flex-col min-w-0">
        <!-- No bot selected -->
        <div
          v-if="!selectedBotId"
          class="h-full flex flex-col items-center justify-center text-center p-8"
        >
          <div
            class="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4"
          >
            <span
              class="i-lucide-bot text-3xl text-slate-400 dark:text-slate-500"
            />
          </div>
          <p class="text-slate-600 dark:text-slate-400 font-medium mb-1">
            {{ t('OMNI.DATA_SOURCES.SELECT_BOT_FIRST') }}
          </p>
          <p class="text-sm text-slate-500 dark:text-slate-500">
            {{ t('OMNI.DATA_SOURCES.SELECT_BOT_HINT') }}
          </p>
        </div>

        <template v-else>
          <!-- Stats Bar -->
          <div class="px-6 pt-5 pb-2">
            <StatsBar
              :stats="stats"
              :sources-count="dataSources.length"
            />
          </div>

          <!-- Tabs -->
          <div class="px-6 pt-2">
            <div class="flex gap-1 border-b border-slate-100 dark:border-slate-800">
              <button
                class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
                :class="
                  activeTab === 'sources'
                    ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                "
                @click="activeTab = 'sources'"
              >
                <span class="i-lucide-files mr-1.5" />
                {{ t('OMNI.DATA_SOURCES.TAB_SOURCES') }}
                <span
                  class="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                >
                  {{ dataSources.length }}
                </span>
              </button>
              <button
                class="px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
                :class="
                  activeTab === 'entries'
                    ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                "
                @click="activeTab = 'entries'"
              >
                <span class="i-lucide-list mr-1.5" />
                {{ t('OMNI.DATA_SOURCES.TAB_ENTRIES') }}
                <span
                  v-if="stats"
                  class="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                >
                  {{ stats.total }}
                </span>
              </button>
            </div>
          </div>

          <!-- Tab Content -->
          <div class="flex-1 overflow-hidden px-6 py-4">
            <!-- Sources Tab -->
            <div
              v-if="activeTab === 'sources'"
              class="h-full overflow-y-auto"
            >
              <div
                v-if="loading"
                class="flex items-center justify-center h-full"
              >
                <span
                  class="i-lucide-loader-2 w-8 h-8 animate-spin text-woot-500"
                />
              </div>

              <div
                v-else-if="!hasSources"
                class="flex flex-col items-center justify-center h-full text-center"
              >
                <div
                  class="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800 mb-4"
                >
                  <span
                    class="i-lucide-file-plus text-3xl text-slate-400 dark:text-slate-500"
                  />
                </div>
                <p
                  class="text-sm text-slate-600 dark:text-slate-400 font-medium mb-1"
                >
                  {{ t('OMNI.DATA_SOURCES.EMPTY_STATE') }}
                </p>
                <div
                  v-if="canEditSection('data_sources')"
                  class="mt-3 flex gap-2"
                >
                  <button
                    class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600"
                    @click="showUploadModal = true"
                  >
                    <span class="i-lucide-upload" />
                    {{ t('OMNI.DATA_SOURCES.ADD_FILE') }}
                  </button>
                  <button
                    class="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-woot-600 bg-woot-50 dark:bg-woot-900/20 rounded-md hover:bg-woot-100 dark:hover:bg-woot-900/30"
                    @click="showApiModal = true"
                  >
                    <span class="i-lucide-globe" />
                    {{ t('OMNI.DATA_SOURCES.ADD_API') }}
                  </button>
                </div>
              </div>

              <div
                v-else
                class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
              >
                <SourceCard
                  v-for="source in dataSources"
                  :key="source.id"
                  :source="source"
                  :status-color="getStatusColor(source.status)"
                  :status-label="getStatusLabel(source.status)"
                  @process="handleProcess"
                  @reprocess="handleReprocess"
                  @delete="handleDeleteSource"
                  @preview="handlePreview"
                  @sync="handleSync"
                  @edit="handleEditSource"
                />
              </div>
            </div>

            <!-- Entries Tab -->
            <div
              v-else-if="activeTab === 'entries'"
              class="h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col shadow-sm"
            >
              <EntriesTable
                :entries="dataEntries"
                :loading="loading"
                :meta="meta"
                :collections="collections"
                :selected-collection="selectedCollection"
                @delete="handleDeleteEntry"
                @bulk-delete="handleBulkDelete"
                @page-change="handlePageChange"
                @search="handleSearch"
                @filter-collection="handleFilterCollection"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Upload Modal -->
    <UploadModal
      :is-open="showUploadModal"
      :uploading="uploading"
      :collections="collections"
      @upload="handleUpload"
      @close="showUploadModal = false"
    />

    <!-- API Source Modal (create + edit) -->
    <ApiSourceModal
      :is-open="showApiModal"
      :collections="collections"
      :bot-id="selectedBotId"
      :source="sourceToEdit"
      @create="handleCreateApiSource"
      @update="handleUpdateApiSource"
      @test-api="handleTestApi"
      @generate-extraction="handleGenerateExtraction"
      @preview-extraction="handlePreviewExtraction"
      @close="handleCloseApiModal"
    />

    <!-- Delete Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50"
      >
        <div
          class="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md mx-4 shadow-2xl border border-slate-200 dark:border-slate-700"
        >
          <div class="flex items-center gap-3 mb-4">
            <div
              class="flex-shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center"
            >
              <span
                class="i-lucide-alert-triangle text-red-500 dark:text-red-400 text-xl"
              />
            </div>
            <h4
              class="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ t('OMNI.DATA_SOURCES.CONFIRM_DELETE_TITLE') }}
            </h4>
          </div>

          <div class="mb-5">
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-3">
              <template v-if="sourceToDelete?.entries_count > 0">
                {{
                  t('OMNI.DATA_SOURCES.CONFIRM_DELETE_MESSAGE', {
                    count: sourceToDelete.entries_count,
                  })
                }}
              </template>
              <template v-else>
                {{ t('OMNI.DATA_SOURCES.CONFIRM_DELETE_MESSAGE_NO_ENTRIES') }}
              </template>
            </p>

            <div
              v-if="sourceToDelete"
              class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
            >
              <div class="flex items-center gap-2 mb-1">
                <span class="i-lucide-file text-slate-400" />
                <span
                  class="font-medium text-slate-900 dark:text-slate-100 text-sm"
                >
                  {{ sourceToDelete.file_name }}
                </span>
              </div>
              <div
                v-if="sourceToDelete.entries_count > 0"
                class="text-xs text-red-500 dark:text-red-400 flex items-center gap-1 mt-1"
              >
                <span class="i-lucide-database" />
                {{ sourceToDelete.entries_count }}
                {{ t('OMNI.DATA_SOURCES.ENTRIES') }}
              </div>
            </div>
          </div>

          <div class="flex gap-2 justify-end">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
              @click="cancelDeleteSource"
            >
              {{ t('OMNI.DATA_SOURCES.CANCEL') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
              @click="confirmDeleteSource"
            >
              {{ t('OMNI.DATA_SOURCES.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
